import { useCaptureEvent } from "@Light/app/analytics";
import { Button } from "@Light/components/page/Button";
import { PageBody } from "@Light/components/page/PageBody";
import { zip5 } from "@Light/utils/address";
import { Lightning01 } from "@untitledui/icons";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { PartnerTosDisclaimer } from "../PartnerTosDisclaimer";
import { useEnrollment } from "../flow/context";
import { EnrollRoute } from "../routes";

export const ZipUtilityIneligible = () => {
  const { requirements, navigate } = useEnrollment();
  const postalCode = requirements.service_address?.value?.postal_code;

  useCaptureEvent({
    event: "enroll_zip_ineligible_viewed",
    condition: true,
    properties: {
      variant: "screen",
      postal_code: postalCode ?? null,
      reason: "area",
    },
  });

  return (
    <BaseZipUtilityIneligible
      postalCode={postalCode}
      onRetry={() => {
        navigate(EnrollRoute.ZipUtilitySearch);
      }}
    />
  );
};

export type IneligibleReason = "area" | "utility";

export function ineligibleCopy(
  reason: IneligibleReason,
  postalCode: string | undefined,
): { title: string; subtitle: string } {
  if (reason === "utility") {
    return {
      title: "Sorry, but we don’t support your utility provider yet",
      subtitle:
        "We’re working on expanding to more utility providers, but can’t serve customers of yours just yet.",
    };
  }
  return {
    title: `Sorry, but we don’t provide service to ${postalCode ? zip5(postalCode) : "this area"}`,
    subtitle:
      "This is either in an area where customers can’t choose providers or we haven’t launched there yet.",
  };
}

export type BaseZipUtilityIneligibleProps = {
  postalCode?: string;
  onRetry?: () => void;
};

export const BaseZipUtilityIneligible = ({
  postalCode,
  onRetry,
}: BaseZipUtilityIneligibleProps) => {
  const { title, subtitle } = ineligibleCopy("area", postalCode);
  return (
    <PageBody
      centered
      icon={Lightning01}
      title={<span data-testid="zip-utility-ineligible-heading">{title}</span>}
      subtitle={subtitle}
      footer={
        <div className="flex flex-col gap-3">
          <EnrollHelpButtonFooter helpButtonText="Is this incorrect? Reach out to us">
            <Button
              size="lg"
              className="w-full"
              onClick={onRetry}
              data-testid="zip-utility-ineligible-retry"
            >
              Use a different zip code
            </Button>
          </EnrollHelpButtonFooter>
          <PartnerTosDisclaimer className="text-center" />
        </div>
      }
    />
  );
};
